import { actions as usersActions } from '@/api/users';
import BasicField from '@payaca/components/basicField/BasicField';
import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';
import { TIconName } from '@payaca/untitled-icons';
import { FunctionComponent, useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import './LoqateAdvisoryEmailInputField.sass';

type Props = {
  styleVariant?: InputStyleVariant;
  name: string;
  value?: any;
  label?: string | JSX.Element;
  description?: string | JSX.Element;
  isDisabled?: boolean;
  isRequired?: boolean;
  iconBefore?: TIconName;
  additionalInputProps?: { [key: string]: any };
  onChange?: (value: { [key: string]: string }) => void;
  onTouch?: (fieldName: string) => void;
  disableValidation?: boolean;
};

const LoqateAdvisoryEmailInputField: FunctionComponent<Props> = ({
  styleVariant,
  name,
  value,
  label,
  description,
  isDisabled = false,
  isRequired = false,
  iconBefore,
  additionalInputProps = {},
  onChange,
  onTouch,
  disableValidation = false,
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const [loqateValidationTimeout, setLoqateValidationTimeout] =
    useState<ReturnType<typeof setTimeout>>();
  const [isValidating, setIsValidating] = useState(false);
  const [isValid, setIsValid] = useState<boolean>();

  useEffect(() => {
    return () => {
      setLoqateValidationTimeout((loqateValidationTimeout) => {
        if (loqateValidationTimeout) {
          clearTimeout(loqateValidationTimeout);
        }
        return undefined;
      });
    };
  }, []);

  const runLoqateValidation = useCallback(
    (value: any) => {
      if (!value || value === '') {
        setIsValid(undefined);
        return;
      }

      setIsValidating(true);
      dispatch(
        usersActions.validateEmail(value, (error: Error, response: any) => {
          setIsValidating(false);
          if (error) {
            setIsValid(undefined);
          } else {
            setIsValid(response);
          }
        })
      );
    },
    [dispatch]
  );

  const iconAfter: TIconName | undefined = useMemo(() => {
    if (isValidating) return 'loading-01.3';
    if (isValid === true) return 'check';
    if (isValid === false) return 'x.3';
  }, [isValidating, isValid]);

  const validationResult = useMemo(() => {
    if (isValidating || isValid === undefined) return;
    return {
      isValid: isValid,
      errors: isValid
        ? undefined
        : [
            'This email looks invalid to us, please double check before continuing',
          ],
    };
  }, [isValidating, isValid]);

  useEffect(() => {
    if (!disableValidation) {
      setLoqateValidationTimeout((loqateValidationTimeout) => {
        if (loqateValidationTimeout) {
          clearTimeout(loqateValidationTimeout);
        }
        return setTimeout(() => {
          runLoqateValidation(value);
        }, 500);
      });
    }
  }, [value, runLoqateValidation, disableValidation]);

  useEffect(() => {
    if (disableValidation) {
      setIsValidating(false);
      setIsValid(undefined);
      setLoqateValidationTimeout((loqateValidationTimeout) => {
        if (loqateValidationTimeout) {
          clearTimeout(loqateValidationTimeout);
        }
        return undefined;
      });
    }
  }, [disableValidation]);

  return (
    <div className="loqate-advisory-email-input-field">
      <ValidatedFieldWrapper
        isTouched={value && value !== ''}
        validationResult={validationResult}
      >
        <BasicField
          styleVariant={styleVariant}
          name={name}
          value={value}
          label={label}
          description={description}
          isDisabled={isDisabled}
          isRequired={isRequired}
          iconBefore={iconBefore}
          additionalInputProps={additionalInputProps}
          onChange={onChange}
          onTouch={onTouch}
          iconAfter={iconAfter}
          type="email"
        />
      </ValidatedFieldWrapper>
    </div>
  );
};

export default LoqateAdvisoryEmailInputField;
